// stores/userStore.js
import { defineStore } from 'pinia';
import * as userApi from '@/api/userApi.js';

const AuthCodes = {
  FAILED: -1,
  SUCCESS: 0,
  ALREADY_EXISTS: 2000,
  USERNAME_EXISTS: 2001,
  EMAIL_EXISTS: 2002,
  LOGIN_EXISTS: 2003,
  PHONE_EXISTS: 2004,
  USER_ACTIVE: 2015,
  USER_INACTIVE: 2016
};

const CryptoConfig = {
  algorithm: { name: 'AES-GCM', length: 256 },
  kdf: {
    name: 'PBKDF2',
    iterations: 100000,
    hash: 'SHA-256',
    saltLength: 16
  },
  ivLength: 12
};

export const useUserStore = defineStore('user', {
  state: () => ({
    authData: null,
    authStatus: null,
    isEmailSent: false,
    lastApiError: null
  }),

  getters: {
    isAuthenticated: (state) => state.authStatus === AuthCodes.SUCCESS && !!state.authData,
	isFreemium: (state) => true,
    status: (state) => state.authStatus,
    user: (state) => state.authData?.user || null,
    data: (state) => state.authData?.data || null,    
    token: (state) => state.authData?.token || null,
    userShortName:  (state) => {
      if (!state.authData?.user) return null
      
      const { first_name } = state.authData.user
      return first_name
    },
    userFullName:  (state) => {
      if (!state.authData?.user) return null
      
      const { first_name, last_name } = state.authData.user
      return [first_name, last_name]
        .filter(Boolean)
        .join(' ')
        .trim();
    }, 
    needsEmailVerification: (state) => 
      state.authStatus === AuthCodes.USER_INACTIVE,
    errorMessage: (state) => {
      switch (state.authStatus) {
        case AuthCodes.ALREADY_EXISTS: return 'Пользователь с такими параметрами уже существует';
        case AuthCodes.USERNAME_EXISTS: return 'Пользователь с таким именем уже существует';
        case AuthCodes.EMAIL_EXISTS: return 'Пользователь с таким email уже существует';
        case AuthCodes.LOGIN_EXISTS: return 'Пользователь с таким логином уже существует';
        case AuthCodes.PHONE_EXISTS: return 'Пользователь с таким телефоном уже существует';
        case AuthCodes.USER_ACTIVE: return 'Пользователь уже активен';
        case AuthCodes.USER_INACTIVE: return 'Требуется подтверждение email';
        case AuthCodes.FAILED: return 'Авторизация не удалась';
        default: return state.lastApiError || 'Произошла ошибка';
      }
    }
  },

  actions: {
    // Криптография
    async _getCryptoKey() {
		const secret = "as876asdfjllkKJosx687HLAKJMCLAqr" //process.env.VUE_APP_CRYPTO_SECRET;
		if (!secret) throw new Error('Crypto secret not defined');
		
		return await crypto.subtle.importKey(
			'raw',
			new TextEncoder().encode(secret),
			CryptoConfig.algorithm,
			true,
			['encrypt', 'decrypt']
		);
    },

    async _encryptData(data) {
		try {
			const key = await this._getCryptoKey();
			const iv = crypto.getRandomValues(new Uint8Array(CryptoConfig.ivLength));
			const encrypted = await crypto.subtle.encrypt({
			...CryptoConfig.algorithm,
			iv
			}, key, new TextEncoder().encode(JSON.stringify(data)));

			return JSON.stringify({
			iv: Array.from(iv),
			data: Array.from(new Uint8Array(encrypted))
			});
		} catch (error) {
			console.error('Encryption failed:', error);
			throw new Error('ENCRYPTION_FAILED');
		}
    },

    async _decryptData(encryptedStr) {
		if (!encryptedStr) return null;

		try {
			const { iv, data } = JSON.parse(encryptedStr);
			const key = await this._getCryptoKey();
			const decrypted = await crypto.subtle.decrypt({
			...CryptoConfig.algorithm,
			iv: new Uint8Array(iv)
			}, key, new Uint8Array(data));

			return JSON.parse(new TextDecoder().decode(decrypted));
		} catch (error) {
			console.error('Decryption failed:', error);
			return null;
		}
    },

    // Работа с данными авторизации
    async _persistAuthData() {
		if (!this.authData) return;
		
		try {
			const encrypted = await this._encryptData(this.authData);
			localStorage.setItem('authData', encrypted);
		} catch {
			this._clearAuthData();
		}
    },

    async _loadAuthData() {
		const stored = localStorage.getItem('authData');
		return stored ? await this._decryptData(stored) : null;
    },

    _clearAuthData() {
		this.authData = null;
		this.authStatus = null;
		localStorage.removeItem('authData');
    },

    // Публичные методы
    async login({ login, password, code }) {
		try {
			const response = await userApi.login(login, password, code);

			if (response.token && response.data) {
				this.authData = {
					token: response.token,
					user: response.data,
					data: response['data-extended'] // Обратите внимание на квадратные скобки
				};
				this.authStatus = AuthCodes.SUCCESS;
				await this._persistAuthData();
				return { success: true };
			}

			this.authStatus = response.status || AuthCodes.FAILED;

			return {
				success: false,
				status: this.authStatus,
				code: response.code,
				message: this.errorMessage
			};
		} catch (error) {
			this._clearAuthData();
			throw error;
		}
    },

    async autoLogin() { // OPTIMIZATION NEEDED!!!
		try {
			const authData = await this._loadAuthData()
			if (!authData?.token) return false

			const response = await userApi.setData(authData.token)
			if (response.id) { // Тут не возвращается ыефегы, только ID, лучше возвращать статус
				this.authData = authData
				this.authStatus = AuthCodes.SUCCESS
				return true
			}
			return false;
		} catch (error) {
			this._clearAuthData()
			return false;
		}
    },

    async register(userData) {
      	try {
			const response = await userApi.register(userData);
			
			if (response.status === AuthCodes.USER_INACTIVE) {
				this.isEmailSent = true;
				return { success: true, needsActivation: true };
			}

			this.authStatus = response.status;
			return { success: response.status === AuthCodes.SUCCESS };
		} catch (error) {
			this.lastApiError = error.message;
			throw error;
      	}
    },

    async activate(key) {
		try {
			const response = await userApi.activate(key);
			if (response.success) {
			this.isEmailSent = false;
			return true;
			}
			throw new Error(response.message || 'Ошибка активации');
		} catch (error) {
			this.lastApiError = error.message;
			throw error;
		}
    },

    async resendCode(email) { // Теперь принимает email напрямую
		try {
			const response = await userApi.resendCode(email);
			this.isEmailSent = true;
			return response;
		} catch (error) {
			this.lastApiError = error.message;
			throw error;
		}
    },

    async restore(email) {
		try {
			await userApi.restore(email);
			return true;
		} catch (error) {
			this.lastApiError = error.message;
			throw error;
		}
    },

    logout() {
		this._clearAuthData()
		this.isEmailSent = false
    },

    // Защищённые методы
    async _callSecureApi(apiFn, ...args) {
		if (!this.token) throw new Error('Требуется авторизация');

		try {
			return await apiFn(this.token, ...args);
		} catch (error) {
			if (error.response?.status === 401) this._clearAuthData();
			this.lastApiError = error.message;
			throw error;
		}
    },
    async uploadAvatar(data) {
		const response = await this._callSecureApi(userApi.uploadAvatar, data);
		if (response && this.authData?.user) {
			this.authData.user.imageFile = response.imageFile; // или другой путь, в зависимости от ответа API
			await this._persistAuthData();
		}
		return response;
    },       
    async removeAvatar(data) {
		const response = await this._callSecureApi(userApi.removeAvatar, data);
		if (response && this.authData?.user) {
			this.authData.user.imageFile = null; // или другое значение по умолчанию
			await this._persistAuthData();
		}
		return response;
    },   
    async recentProjects() {
      	return this._callSecureApi(userApi.recentProjects);
    },

    async changePassword(passwordData) {
      	return this._callSecureApi(userApi.changePassword, passwordData);
    },

    async saveSettings(settings) {
		const response = await this._callSecureApi(userApi.saveSettings, settings);
		if (response && this.authData?.user) {
			this.authData.user = { ...this.authData.user, ...response.updatedUser };
			await this._persistAuthData();
		}
		return response;
    }
  }
})